import React, { useEffect, useContext } from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { MLHVQBridgeService, MLStateRolloutService } from 'ml-services-common-react'
import { MLModalService } from 'ml-portals-services-react';

import mlAccountsModalMessages from '../../components/MLPossibleAccountsModal/MLPossibleAccountsModal.messages';
import mlPossibleAccountsModal from '../../components/MLPossibleAccountsModal/MLPossibleAccountsModal.metadata.json5';
import mlAccountsModalStyles from '../../components/MLPossibleAccountsModal/MLPossibleAccountsModal.module.scss';

import messages from './MLGatewayHVQLandingPage.messages';

function MLGatewayHVQLandingPage(props) {
    const {
        history,
        match: {
            params: { hvqTransactionId }
        }
    } = props;
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const [showModal, setShowModal] = React.useState(false);
    const [matchedAccounts, setMatchedAccounts] = React.useState([]);

    const {
        showProgressDialog,
        showErrorModal
    } = MLModalService();

    const parseErrorForMessage = (error) => {
        const errorMessageStringArray = error.baseError.split(' ');
        const outerErrorCodeIndex = error.baseError.indexOf('ErrorCode:');
        const outerErrorCodeValue = outerErrorCodeIndex !== -1 ? errorMessageStringArray[outerErrorCodeIndex + 1] : undefined;
        const outerErrorCodeNumericValue = !_.isUndefined(outerErrorCodeValue) ? parseInt(outerErrorCodeValue, 10) : undefined;

        let errorMessage;

        if (outerErrorCodeNumericValue === appConfig.errorCodes.DISPLAYABLE_ERROR_ML) {
            const errorString = error.baseError;
            const indexOfMessages = errorString.indexOf('Messages_ML');
            let errorMessages = errorString.substring(indexOfMessages).split(']')[0];
            errorMessages = errorMessages.split('[')[1];
            errorMessage = errorMessages.split(',')[0];
        }

        return errorMessage;
    };

    const getErrorMessageToShow = (error, quoteID) => {
        const defaultErrorMessage = translator(messages.unableToBridge, { quoteID : quoteID });
        const sb = [];
        const httpError = _.get(error, 'baseError.data.error');
        const errorMessage = parseErrorForMessage(error);
        try {
            if (!_.isUndefined(httpError) && httpError.messages.length > 0) {
                httpError.messages.forEach((message) => {
                    sb.push(message);
                });
            } else if(!_.isUndefined(errorMessage)){
                sb.push(errorMessage);
            } else {
                sb.push(defaultErrorMessage);
            }
        } catch (e) {
            sb.push(defaultErrorMessage);
        }

        return sb.join(' ');
    };

    const handleBridgingError = (quoteID, error) => {
        const title = translator(messages.errorBridgingOperation);
        console.error(error);
        showErrorModal(title, getErrorMessageToShow(error, quoteID));
    };

    const handleBridgingRequest = (hvqBridgingCall) => {
        setShowModal(false);
        const bridgePromise = hvqBridgingCall();
        showProgressDialog(messages.bridgingInProgress, bridgePromise);
        bridgePromise.then((hvqRes) => {
            if (!_.isUndefined(hvqRes)) {
                MLStateRolloutService.checkIsCRWizardEligible(hvqRes.jobID, authHeader).then((isCRWizardEligible) => {
                    if (isCRWizardEligible) {
                        history.push('/quoteentry', { account: hvqRes.accountNumber, quoteID: hvqRes.jobID });
                    }else{
                        history.push(`/accounts/${hvqRes.accountNumber}/summary`, {
                            accountNumber: hvqRes.accountNumber,
                            bridgedQuoteNumber: hvqRes.jobID
                        });
                    }
                }).catch(() => {
                    // Error determining if User has access to CompRater Wizard. Assume no.
                    history.push(`/accounts/${hvqRes.accountNumber}/summary`, {
                        accountNumber: hvqRes.accountNumber,
                        bridgedQuoteNumber: hvqRes.jobID
                    });
                }).finally(() => {
                    setShowModal(false);
                })
            } else {
                handleBridgingError(hvqTransactionId);
            }

        }, (error) => {
            handleBridgingError(hvqTransactionId, error);
        });
    };

    const createNewAccount = () => {
        handleBridgingRequest(
            () => MLHVQBridgeService.createFromHVQTransactionIdOnNewAccount(hvqTransactionId, authHeader)
        );
    };

    const getPotentialAccountsModal = () => {
        const potentialAccountsOverrideProps = () => {
            const getSuffixName = (suffixCode) => {
                const nameSuffixList = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
                const suffixTypeKeyName = nameSuffixList.getCode(suffixCode).name;
                const suffixDisplayName = translator(
                    {
                        id: suffixTypeKeyName,
                        defaultMessage: ' '
                    }
                );
                return suffixDisplayName;
            };

            const getFullName = (accountHolder) => {
                const middleName = accountHolder.middleName || '';
                const suffix = accountHolder.suffix ? getSuffixName(accountHolder.suffix) : '';
                return `${accountHolder.firstName} ${middleName} ${accountHolder.lastName} ${suffix}`;
            };

            const overrides = matchedAccounts.map((account, index) => {

                const goToAccountSummary = () => {
                    setShowModal(false);
                    history.push(`/accounts/${account.accountNumber}/summary`);
                };

                const startNewQuote = ()=> {
                    handleBridgingRequest(
                        () => MLHVQBridgeService.createFromHVQTransactionIdOnExistingAccount(hvqTransactionId, account.accountNumber, authHeader)
                    );
                    return true;
                };

                return {
                    [`accountNumberLink${index}`]: {
                        onClick: goToAccountSummary,
                        content: _.capitalize(getFullName(account.accountHolder))
                    },
                    [`autoProduct${index}`]: {
                        visible: !_.isUndefined(account.numberOfAutoProducts_ML) && account.numberOfAutoProducts_ML > 0,
                        content: translator(mlAccountsModalMessages.numberOfProductsAuto, { numberOfProducts: account.numberOfAutoProducts_ML })
                    },
                    [`homeProduct${index}`]: {
                        visible: !_.isUndefined(account.numberOfHomeProducts_ML) && account.numberOfHomeProducts_ML > 0,
                        content: translator(mlAccountsModalMessages.numberOfProductsHome, { numberOfProducts: account.numberOfHomeProducts_ML })
                    },
                    [`umbrellaProduct${index}`]: {
                        visible: !_.isUndefined(account.numberOfUmbrellaProducts_ML) && account.numberOfUmbrellaProducts_ML > 0,
                        content: translator(mlAccountsModalMessages.numberOfProductUmbrella, { numberOfProducts: account.numberOfUmbrellaProducts_ML })
                    },
                    [`address${index}`]: {
                        content: _.get(account, 'accountHolder.primaryAddress.displayName', '')
                    },
                    [`phone${index}`]: {
                        content: _.get(account, 'accountHolder.maskedPrimaryPhoneNumber_ML', '')
                    },
                    [`selectButton${index}`]: {
                        onClick: startNewQuote
                    }
                };
            });

            return Object.assign({}, ...overrides);
        };

        const overrides = {
            possibleAccountsHeader: {
                visible: false,
            },
            potentialAccountsIterable: {
                data: matchedAccounts
            },
            
            ...potentialAccountsOverrideProps()
        };

        const componentProps = {
            title: translator(mlAccountsModalMessages.existingCustomersHeader),
            uiProps: mlPossibleAccountsModal.componentContent,
            actionBtnLabel: mlAccountsModalMessages.addNewCustomerButton,
            viewModel: {},
            modalBodyStyles: mlAccountsModalStyles,
            additionalPEStyle: 'customPossibleAccountsModal',
            overrideProps: overrides,
            onSaveAction: () => {
                createNewAccount();
                return true;
            },
            removeSidesPadding: true,
            messageNextToButton: translator(mlAccountsModalMessages.noneAreMatch),
            buttonType: 'filled',
            isOpen: showModal,
            onCancelAction: () => setShowModal(false)
        };

        return <FormModalPopover {...componentProps} />
    };

    useEffect(() => {
        const promise = MLHVQBridgeService.getPotentialExistingAccountsByHVQTransactionId(hvqTransactionId, authHeader);
        showProgressDialog(platformMessages.loading, promise);
        promise.then((potentialExistingAccounts) => {
            if (potentialExistingAccounts.length > 0) {
                setMatchedAccounts(potentialExistingAccounts);
                setShowModal(true);
            } else {
                createNewAccount();
            }
        }, (error) => {
            handleBridgingError(hvqTransactionId, error);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (showModal ? getPotentialAccountsModal(): true);
}

export default withRouter(MLGatewayHVQLandingPage);
